import {
  documentId,
  query,
  getDoc,
  getDocs,
  onSnapshot,
  where,
  orderBy,
  limit,
  getCountFromServer
} from "firebase/firestore";
import { postApi, requestApi } from "@/api";
import {PersistentModel} from "@/models/PersistentModel";
import {toMoment} from "@/dateUtils";
import {User} from "@/models/User";
import Correspondence from "@/models/Correspondence";
import {
  getListingDeferralEndDate,
  hasListingDeferral,
  hasListingDeferralEnded
} from "@strenforcement/domain/listing";

const DeferralResponseKeyByCollection = Object.freeze({
  listings: 'listing',
  properties: 'property'
});

const CorrespondenceResponseKeyByCollection = Object.freeze({
  listings: 'listing',
  properties: 'property'
});

/**
 * Base model class for providing a consistent interface to a document within a sub-collection of a city.
 */
export class PersistentCityModel extends PersistentModel {

  static dataConverter(city, collectionName) {
    collectionName = collectionName || this.collectionName;
    return ({id, data}) => {
      return new this(city, {
        ref: city.subDoc(collectionName, id),
        data
      });
    };
  }

  static docConverter(city) {
    return (doc) => new this(city, doc);
  }

  static snapshotConverter(city) {
    return (snapshot) => snapshot.docs.map(this.docConverter(city));
  }

  static list(city, collectionName, constraints = []) {
    collectionName = collectionName || this.collectionName;
    let q = city.subCollection(collectionName);
    constraints?.forEach(constraint => {
      if (constraint instanceof Array) {
        q = query(q, where(...constraint));
      } else {
        q = query(q, constraint);
      }
    });
    return getDocs(q)
    .then(this.snapshotConverter(city));
  }

  static one(city, id, collectionName) {
    collectionName = collectionName || this.collectionName;
    return getDoc(city.subDoc(collectionName, id))
    .then(this.docConverter(city));
  }

  static oneFromApi(city, { id, data }) {
    return this.dataConverter(city)({ id, data });
  }

  static some(city, idPrefix, collectionName) {
    collectionName = collectionName || this.collectionName;
    return getDocs(query(
      city.subCollection(collectionName),
      where(documentId(), '>=', idPrefix),
      where(documentId(), '<', idPrefix+'~')
    ))
    .then(this.docConverter(city));
  }

  static onOne(city, id, handler, collectionName) {
    collectionName = collectionName || this.collectionName;
    const convert = this.docConverter(city);
    return onSnapshot(
      city.subDoc(collectionName, id),
      doc => handler(convert(doc))
    );
  }

  static query(city, { constraints, sort, sortDirection, maxResults }) {
    let qCount = city.subCollection(this.collectionName);
    constraints?.forEach(constraint => {
      if (constraint instanceof Array) {
        qCount = query(qCount, where(...constraint));
      } else {
        qCount = query(qCount, constraint);
      }
    });
    let resultsArgs = [];
    if (sort && sortDirection) {
      resultsArgs.push(orderBy(sort, sortDirection));
    }
    if (maxResults) {
      resultsArgs.push(limit(maxResults));
    }
    const qResults = resultsArgs.length === 0 ? qCount : query(qCount, ...resultsArgs);
    return Promise.all([
      getCountFromServer(qCount).then(snapshot => snapshot.data().count),
      getDocs(qResults).then(this.snapshotConverter(city))
    ]).then(values => {
      return {
        count: values[0],
        results: values[1]
      };
    });
  }

  static async count(city, constraints = []) {
    let qCount = city.subCollection(this.collectionName);
    constraints?.forEach(constraint => {
      if (constraint instanceof Array) {
        qCount = query(qCount, where(...constraint));
      } else {
        qCount = query(qCount, constraint);
      }
    });
    const snapshot = await getCountFromServer(qCount)
    return snapshot.data().count;
  }

  constructor(city, doc) {
    super(doc);
    this.city = city;
  }

  async collection() {
    return this.city.subCollection(this.constructor.collectionName);
  }

  copy() {
    return this.constructor.dataConverter(this.city)({
      id: this.id,
      data: JSON.parse(JSON.stringify(this._data))
    });
  }

  doApiCall(name, data) {
    return super.doApiCall(name, {
      ...data,
      cityId: this.city.id
    });
  }

  indexKeys() {
    return [];
  }

  //
  // --- Notes ---
  //

  get notes() {
    return this._get('notes') || '';
  }

  updateNotes(notes) {
    return this.update({notes}, 'notes', true);
  }

  //
  // --- Whitelist ---
  //

  async updateWhitelisted(whitelisted, reason, expiration) {
    const collectionName = this.constructor.collectionName;
    const responseKey = DeferralResponseKeyByCollection[collectionName];
    if (!responseKey) {
      throw new Error(`Deferral endpoint is not supported for "${collectionName}"`);
    }

    const response = await postApi(`/v1/cities/${this.city.id}/${collectionName}/${this.id}/deferral`, {
      whitelisted,
      reason,
      expiration
    });

    if (response?.[responseKey]?.data) {
      this._data = response[responseKey].data;
      this.updateCount++;
      return this;
    }
    return await this.reload();
  }

  get isWhitelisted() {
    let whitelisted = this._get('whitelisted') || false;
    if (whitelisted) {
      let expiration = this.whitelistExpiration;
      return !expiration || expiration.isAfter();
    }
    return false;
  }

  get hasDeferral() {
    return hasListingDeferral({
      whitelisted: this._get('whitelisted') || false
    });
  }

  get hasEndedDeferral() {
    return hasListingDeferralEnded({
      whitelisted: this._get('whitelisted') || false,
      whitelistExpiration: this.whitelistExpiration,
      now: new Date()
    });
  }

  get deferralEndDate() {
    const date = getListingDeferralEndDate({
      whitelisted: this._get('whitelisted') || false,
      whitelistExpiration: this.whitelistExpiration
    });
    return date ? toMoment(date) : null;
  }

  get whitelistReason() {
    return this._get('whitelistReason') || '';
  }

  get whitelistExpiration() {
    const expiration = this._get('whitelistExpiration');
    return expiration ? toMoment(expiration) : null;
  }

  get whitelistUpdatedAt() {
    return toMoment(this._get('whitelistUpdatedAt'));
  }

  get whitelistUpdatedBy() {
    const user = this._get('whitelistUpdatedBy');
    return user ? new User(user) : null;
  }

  //
  // --- Correspondences (Letters) ---
  //

  async addCorrespondence({ template, options, listingId }) {
    const collectionName = this.constructor.collectionName;
    const responseKey = CorrespondenceResponseKeyByCollection[collectionName];
    if (!responseKey) {
      throw new Error(`Letter endpoint is not supported for "${collectionName}"`);
    }

    const response = await postApi(`/v1/cities/${this.city.id}/${collectionName}/${this.id}/correspondences`, {
      template,
      options: options || {},
      listingId: listingId || null
    });

    if (response?.[responseKey]?.data) {
      this._data = response[responseKey].data;
      this.updateCount++;
      return this;
    }
    return await this.reload();
  }

  async removeCorrespondence(correspondence) {
    const collectionName = this.constructor.collectionName;
    const responseKey = CorrespondenceResponseKeyByCollection[collectionName];
    if (!responseKey) {
      throw new Error(`Letter endpoint is not supported for "${collectionName}"`);
    }

    const correspondenceId = correspondence?.id;
    if (!correspondenceId) {
      throw new Error('letter.id is required');
    }

    const response = await requestApi(`/v1/cities/${this.city.id}/${collectionName}/${this.id}/correspondences/${encodeURIComponent(correspondenceId)}`, {
      method: 'DELETE'
    });

    if (response?.[responseKey]?.data) {
      this._data = response[responseKey].data;
      this.updateCount++;
      return this;
    }
    return await this.reload();
  }

  async updateCorrespondence(correspondence) {
    const collectionName = this.constructor.collectionName;
    const responseKey = CorrespondenceResponseKeyByCollection[collectionName];
    if (!responseKey) {
      throw new Error(`Letter endpoint is not supported for "${collectionName}"`);
    }

    const correspondenceId = correspondence?.id;
    if (!correspondenceId) {
      throw new Error('letter.id is required');
    }

    correspondence.saving = true;
    try {
      const response = await requestApi(`/v1/cities/${this.city.id}/${collectionName}/${this.id}/correspondences/${encodeURIComponent(correspondenceId)}`, {
        method: 'PUT',
        body: {
          correspondence: correspondence.serialize()
        }
      });

      if (response?.[responseKey]?.data) {
        this._data = response[responseKey].data;
        this.updateCount++;
        return this;
      }
      return await this.reload();
    } finally {
      correspondence.saving = false;
    }
  }

  /**
   * @return {Correspondence[]}
   */
  get correspondences() {
    return (this._data.correspondences || []).map(data => new Correspondence(this, data));
  }

}
